import { SearchOutlined } from '@vindr/icons';
import { useGetDevices } from '@vindr/services/actions/devices.action';
import { useGetInstitutions } from '@vindr/services/actions/institution.action';
import { useCreateOrderGroup } from '@vindr/services/actions/order.action';
import { useGetPatientsVisited } from '@vindr/services/actions/patient.action';
import { useGetDepartments } from '@vindr/services/actions/department.action';
import { ParamsCreateOrderGroup } from '@vindr/services/apis/order.api';
import { DeviceTypes, GENDER } from '@vindr/services/constants/common.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { ORDER_PRIORITY } from '@vindr/services/constants/order.constant';
import { convertPhoneNumber } from '@vindr/utils/formatText/formatText';
import { Pick, getDateFormatted, uuidv4 } from '@vindr/utils/helpers/common.helper';
import { useDebounce } from '@vindr/utils/hooks/useDebounce';
import { Button, Col, DatePicker, Form, Input, Modal, Row, Select, Typography } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GENDER_OPTIONS } from '@vindr/ui/src/components/Templates/Select';
import PatientSearch from './patient/PatientSearch';
import ProcedureSearch from './procedure/ProcedureSearch';
import useProcedureColumns from './useProcedureColumns';
import { FormItem, Table } from '@vindr/ui';

const { Title } = Typography;

export interface CreateOrderForm {
  institutionId?: string | null;
  defaultInstitutionId?: string | null;
  onFinish: (orderIds: string[]) => void;
  onCancel: () => void;
  defaultValues?: {
    birth_date: string;
    device_id: string;
    diagnostic?: string;
    order_doctor_name?: string;
    order_group?: string;
    patient_id: string;
    patient_name: string;
    refer_doctor_name?: string;
    sex: GENDER;
    visit_id?: string;
  };
  disablePatientSearch?: boolean;
  message: MessageInstance;
}

const CreateOrderForm = ({
  institutionId = null,
  onFinish,
  onCancel,
  defaultValues,
  disablePatientSearch = false,
  message,
  defaultInstitutionId = null,
}: CreateOrderForm) => {
  const { t } = useTranslation('Vindoc');
  const [popupProcedureSearch, setPopupProcedureSearch] = useState(false);
  const [popupPatientSearch, setPopupPatientSearch] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(defaultValues);
  // const [searchPatientField, setSearchPatientField] = useState('patient_id');
  const [patientFilter, setPatientFilter] = useState({});

  const [orderForm] = Form.useForm();
  const [patientForm] = Form.useForm();
  const [procedureList, setProcedureList] = useState([]);
  const [searchInstitution, onSearchInstitution] = useDebounce<string>();
  const [currentModalityByDevice, setCurrentModalityByDevice] = useState(null);
  const { data: institutions } =
    useGetInstitutions({ offset: 0, limit: 100, name: searchInstitution })?.data || {};
  const institution_id = Form.useWatch('institution_id', orderForm);

  const [searchDevice, onSearchDevice] = useDebounce<string>();
  const { data: devices } =
    useGetDevices({
      offset: 0,
      limit: 100,
      institution_ids: [institution_id],
      active: true,
      types: [DeviceTypes.Modality],
      name: searchDevice,
    })?.data || {};
  const departments = useGetDepartments({
    offset: 0,
    limit: 100,
    institution_ids: [institution_id],
    active: true,
  });

  const departmentsData = useMemo(() => departments?.data?.data || [], [departments]);

  const procedureColumns = useProcedureColumns({
    onRemove: (record) =>
      setProcedureList(
        procedureList.filter((it) => it?.id != record.id || it?.timestamp != record?.timestamp)
      ),
  });
  const { mutate: createOrderGroup, isLoading } = useCreateOrderGroup();

  // ? Study and selected Order not have order id, so we need call api getPatientsVisited and get order id
  const { data: patient } =
    useGetPatientsVisited(
      {
        offset: 0,
        limit: 100,
        visit_id: defaultValues?.visit_id,
        patient_name: defaultValues?.patient_name,
        patient_id: defaultValues?.patient_id,
      },
      { enabled: !!defaultValues?.visit_id || !!defaultValues?.patient_id }
    )?.data || {};

  useEffect(() => {
    if (!patient || !defaultValues) return;

    // ? Have more than patient when same visit_id. Example: PVI23040008 and PVI23040008abc
    const patientId =
      patient.length > 1
        ? patient.find((it) => it?.visit_id === defaultValues?.visit_id)?.id
        : patient?.[0]?.id;

    setSelectedPatient((prev) => ({ ...prev, id: patientId }));
  }, [patient, defaultValues]);

  useEffect(() => {
    if (!defaultValues) return;

    const orderDefault = Pick(defaultValues, [
      'device_id',
      'refer_doctor_name',
      'order_doctor_name',
      'diagnostic',
    ] satisfies (keyof typeof defaultValues)[]);

    Object.entries(orderDefault).forEach(([key, value]) => {
      orderForm.setFieldValue(key, value);
    });
  }, [defaultValues, orderForm]);

  useEffect(() => {
    patientForm.setFieldsValue({
      ...selectedPatient,
      birth_date: selectedPatient?.birth_date ? dayjs(selectedPatient?.birth_date) : null,
    });
  }, [patientForm, selectedPatient]);

  useEffect(() => {
    if (institution_id) {
      setPatientFilter({
        institution_ids: [institution_id],
      });
    }
  }, [institution_id]);

  useEffect(() => {
    if (!devices || !devices.length) return;
    const deviceId = orderForm.getFieldValue('device_id');

    if (!devices.find((it) => it?.id == deviceId)) {
      orderForm.setFieldValue('device_id', null);
    }
  }, [devices, orderForm]);

  const handleSave = () => {
    const procedure_ids = procedureList.map((it) => it.id);

    if (!procedure_ids.length) {
      message.error(t(MESSAGE.ERROR.EMSG_8018));

      return;
    }

    Promise.all([patientForm.validateFields(), orderForm.validateFields()])
      .then(([patient, order]) => {
        const input: ParamsCreateOrderGroup = {
          ...order,
          procedure_ids,
          visited_patient: {
            ...selectedPatient,
            ...patient,
            phone: convertPhoneNumber(patient?.phone || selectedPatient?.phone),
          },
        };

        createOrderGroup(input, {
          onSuccess: (data) => {
            const orderIds = data?.data?.order_ids;

            message.success(t(MESSAGE.SUCCESS.SCD_8000));
            onFinish(orderIds);
          },
          onError: (error: any) => {
            message.error({
              content: t(
                MESSAGE.ERROR[error?.response?.data?.error_code] || MESSAGE.ERROR.EMSG_0001
              ),
            });
          },
        });
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
      });
  };

  useEffect(() => {
    orderForm.setFieldValue(
      'institution_id',
      institutionId || defaultInstitutionId || institutions?.[0]?.id
    );
  }, []);

  return (
    <div>
      <Row gutter={[36, 36]}>
        <Col xl={12} xs={24}>
          <Row className="mb-5">
            <Col className="mb-0 flex items-center" span={12}>
              <Title className="mb-0" level={3}>
                {t('Patient Information')}
              </Title>
            </Col>
            <Col span={12}>
              <div className="flex justify-end">
                <Button
                  className="flex items-center px-2"
                  disabled={disablePatientSearch}
                  onClick={() => setPopupPatientSearch(true)}
                >
                  {t('Patient Search')}
                  <SearchOutlined className="fill-[#000] dark:fill-[#fff]" />
                </Button>
              </div>
            </Col>
          </Row>
          <div>
            <Form form={orderForm} layout="vertical" name="form" onFinish={handleSave}>
              <FormItem.InstitutionList
                required
                showSearch
                allowClear={false}
                disabled={!!selectedPatient || !!institutionId}
                multiple={false}
                name="institution_id"
                placeholder={t('Select Institution')}
                wrapperClassName="mb-2 w-full"
                onSearch={onSearchInstitution}
                onChange={() => {
                  patientForm.resetFields();
                  setSelectedPatient(null);
                }}
              />
            </Form>
            <Form
              className="form-filter-condition"
              form={patientForm}
              layout="vertical"
              name="form"
              onFinish={handleSave}
            >
              <FormItem.Input
                required
                className="mb-2"
                label={t('Patient Name')}
                name="patient_name"
                placeholder={t('Enter Patient Name')}
              />
              <Row gutter={16}>
                <Col sm={12} xs={24}>
                  <FormItem.Birthday required className="mb-2" name="birth_date" />
                </Col>
                <Col sm={12} xs={24}>
                  <Form.Item
                    required
                    className="mb-2"
                    initialValue={GENDER.Other}
                    label={t('Gender')}
                    name="sex"
                    rules={[{ required: true, message: t('Required value') }]}
                  >
                    <Select
                      getPopupContainer={(trigger) => trigger.parentElement}
                      placeholder={t('Select')}
                      options={GENDER_OPTIONS.map((gender) => ({
                        ...gender,
                        label: t(gender.label),
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col sm={12} xs={24}>
                  <Form.Item className="mb-2" label={t('Patient ID')} name="patient_id">
                    <Input placeholder={t('Enter Patient ID')} />
                  </Form.Item>
                </Col>
                <Col sm={12} xs={24}>
                  <Form.Item className="mb-2" label={t('Visit ID')} name="visit_id">
                    <Input
                      disabled={!!defaultValues?.visit_id || !!selectedPatient?.visit_id}
                      placeholder={t('Enter Visit ID')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col className="w-full" sm={12} xs={24}>
                  <FormItem.PhoneNumber className="mb-2" />
                </Col>
                <Col sm={12} xs={24}>
                  <FormItem.Email className="mb-2" />
                </Col>
              </Row>
              <FormItem.InputArea
                className="min-h-[166px]"
                formClassName="mb-0"
                label={t('Address')}
                name="address"
                placeholder={t('Enter Address')}
              />
            </Form>
          </div>
        </Col>
        <Col className="flex flex-col" xl={12} xs={24}>
          <Title className="mb-6" level={3}>
            {t('Order Information')}
          </Title>
          <Form form={orderForm} layout="vertical" name="form" onFinish={handleSave}>
            <Form.Item
              className="mb-2"
              initialValue={dayjs()}
              label={t('Scheduled Date')}
              name="scheduled_date"
            >
              <DatePicker
                showTime
                className="w-full"
                format={getDateFormatted(true)}
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col sm={12} xs={24}>
                <FormItem.Select
                  required
                  searchVietnamese
                  showSearch
                  className="mb-0"
                  label={t('Device')}
                  name="device_id"
                  options={devices?.map((it) => ({ label: it?.name, value: it?.id }))}
                  placeholder={t('Select Device')}
                  wrapperClassName="mb-2"
                  onSearch={onSearchDevice}
                  onChange={(value) => {
                    const device = devices.find((it) => it?.id === value);
                    setCurrentModalityByDevice(device?.modality);
                  }}
                  onClear={() => {
                    onSearchDevice(null);
                  }}
                />
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item className="mb-2" label={t('Order Group')} name="order_group">
                  <Input placeholder={t('Enter Order Group')} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col sm={12} xs={24}>
                <FormItem.Select
                  className="mb-0"
                  label={t('Order Department')}
                  name="department_id"
                  options={departmentsData?.map((it) => ({ label: it?.name, value: it?.id }))}
                  placeholder={t('Select Order Department')}
                  wrapperClassName="mb-2"
                />
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item className="mb-2" label={t('Order Doctor')} name="order_doctor_name">
                  <Input placeholder={t('Enter Order Doctor')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item className="mb-9" label={t('Diagnoses')} name="diagnostic">
              <Input placeholder={t('Enter Diagnoses')} />
            </Form.Item>
            <Form.Item hidden initialValue={ORDER_PRIORITY.MEDIUM} name="priority">
              <i />
            </Form.Item>
          </Form>
          <Table
            columns={procedureColumns}
            dataSource={procedureList || []}
            pagination={null}
            rowKey={() => uuidv4()}
            style={{ background: '#2c5166', height: '100%' }}
            wrapperClassName="procedure-table grow-1 shrink basis-[auto] min-h-[202px]"
          />
          <Button className="w-full" onClick={() => setPopupProcedureSearch(true)}>
            {t('Add Procedure')}
          </Button>
          <Modal
            destroyOnClose
            className="vindr-modal patient-search max-w-screen"
            footer={null}
            maskClosable={false}
            open={popupPatientSearch}
            title={t('Patient Search')}
            width="90%"
            zIndex={1001}
            onCancel={() => setPopupPatientSearch(false)}
          >
            <PatientSearch
              initFilters={patientFilter}
              onCancel={() => setPopupPatientSearch(false)}
              onSave={(patient) => {
                setSelectedPatient(patient);
                setPopupPatientSearch(false);
              }}
            />
          </Modal>
          <Modal
            destroyOnClose
            className="vindr-modal procedure-search max-w-screen w-[900px]"
            footer={null}
            maskClosable={false}
            open={popupProcedureSearch}
            title={t('Procedure Search')}
            zIndex={1001}
            onCancel={() => setPopupProcedureSearch(false)}
          >
            <ProcedureSearch
              institution_id={institution_id}
              modality={currentModalityByDevice}
              onCancel={() => setPopupProcedureSearch(false)}
              onSave={(procedures) => {
                setProcedureList([...procedureList, ...procedures]);
                setPopupProcedureSearch(false);
              }}
            />
          </Modal>
        </Col>
      </Row>
      <div className="footer mt-3 flex justify-end">
        <Button className="mr-1" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button loading={isLoading} type="primary" onClick={handleSave}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default memo(CreateOrderForm, (prevProps, nextProps) => {
  return isEqual(prevProps.defaultValues, nextProps.defaultValues);
});
